"use client"

import type { KeyboardEvent } from "react"

import { cn } from "@/lib/utils"

type Presentation = "display" | "body"

interface EditableTextProps {
  value: string
  onChange: (value: string) => void
  /** Fired on blur — the commit point for persistence. */
  onCommit?: () => void
  /**
   * `display` reads as an editorial title (display type, one paragraph —
   * newlines are stripped); `body` reads as a written note.
   */
  presentation?: Presentation
  placeholder?: string
  /** Required — an edit-surface field carries no visible label. */
  ariaLabel: string
  isInvalid?: boolean
  /**
   * Whether the hover/focus ground bleeds past the text edge (the full-line
   * highlight of the manuscript words). Off inside a card, where the card
   * already provides the padding. Defaults to on.
   */
  bleed?: boolean
  /**
   * Renders the text at rest with no editability signifiers and a non-editable
   * field.
   */
  readOnly?: boolean
  className?: string
}

const PRESENTATION: Record<Presentation, string> = {
  display: "text-display-xs sm:text-display-sm text-primary font-semibold",
  body: "text-md text-secondary leading-normal",
}

/**
 * An inline edit-surface text field. It reads as finished content at rest and
 * carries a *persistent* (touch- and pointer-visible) editability signifier — a
 * faint baseline rule — that deepens to a warm ground on hover and a ground +
 * Byline Blue ring on focus. The thing you edit looks like the thing you
 * publish (see docs/design/edit-surfaces.md).
 *
 * A native auto-sizing <textarea> so a long title wraps like a heading;
 * `display` strips newlines so a title stays a single line of thought.
 */
export function EditableText({
  value,
  onChange,
  onCommit,
  presentation = "body",
  placeholder,
  ariaLabel,
  isInvalid = false,
  bleed = true,
  readOnly = false,
  className,
}: EditableTextProps) {
  const singleLine = presentation === "display"

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (singleLine && e.key === "Enter") {
      e.preventDefault()
      e.currentTarget.blur()
    }
  }

  return (
    <div
      className={cn(
        "rounded-md py-1.5",
        bleed ? "-mx-2 px-2" : "px-0",
        // The editability signifiers (baseline rule, hover ground, focus ring)
        // only make sense when the text can actually be edited.
        !readOnly && [
          "border-secondary border-b transition-colors duration-150 motion-reduce:transition-none",
          "hover:bg-surface-grouped/60",
          "focus-within:bg-surface-grouped focus-within:border-transparent focus-within:ring-2 focus-within:ring-[color:var(--color-focus-ring)] focus-within:ring-inset",
        ],
        isInvalid && "ring-error_subtle ring-1",
        className,
      )}
    >
      <textarea
        aria-label={ariaLabel}
        aria-invalid={isInvalid || undefined}
        value={value}
        rows={singleLine ? 1 : 2}
        placeholder={readOnly ? undefined : placeholder}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        onKeyDown={readOnly ? undefined : handleKeyDown}
        onBlur={readOnly ? undefined : onCommit}
        onChange={
          readOnly
            ? undefined
            : (e) =>
                onChange(
                  singleLine
                    ? e.target.value.replace(/[\n\r]+/g, " ")
                    : e.target.value,
                )
        }
        className={cn(
          "placeholder:text-placeholder block [field-sizing:content] w-full resize-none border-0 bg-transparent p-0 outline-none focus:ring-0",
          PRESENTATION[presentation],
        )}
      />
    </div>
  )
}
